import intersection from 'lodash-es/intersection';
import { Category } from '@df/catalog/category/category';
import { Product } from '@df/catalog/product/product';
import { Filter, IFilterConfig } from '@df/catalog-ui/listing/filter/filter';
import { ListingFilter } from '@df/catalog-ui/listing/listing';

export interface IFilterCategoryConfig extends IFilterConfig<number> {
    /**
     * Categories source:
     * subcategories or default
     */
    source?: string;
}

@ListingFilter({
    filterType: 'category'
})
export class FilterCategory<T extends Product = Product> extends Filter<T, number[], number, IFilterCategoryConfig> {
    /**
     * Category filter alway has matches
     */
    hasAnyMatches = true;

    /**
     * Initial selection
     */
    protected override _selection: number[] = [];

    /**
     * Source category getter
     */
    get model() {
        return this.app.resources.category.getExisting(this.id);
    }

    /**
     * Get categories from category model based on filter source.
     *
     * Current available options: subcategories
     */
    get options(): Category[] {
        const model = this.model;
        if (!model) {
            return [];
        }
        if (this.config.source === 'subcategories') {
            return model.children || [];
        }
        return model.siblings || [];
    }

    /**
     * Resets selection
     */
    reset() {
        if (this.hasSelection) {
            this.selection = [];
            return this.next();
        }
    }

    /**
     * Selects option. Will unselect any other options if requested so.
     */
    select(category: Category, unselectOther: boolean = false): void {
        if (unselectOther) {
            this._selection = [category.id];
        } else {
            if (!this.isSelected(category)) {
                this._selection.push(category.id);
            }
        }

        this.next();
    }

    /**
     * Unselects option.
     */
    unselect(category: number | Category): void {
        const value = typeof category === 'number' ? category : category.id;
        this._selection = this._selection.filter(selectedOptionId => selectedOptionId !== value);
        this.next();
    }

    /**
     * Toggles option. Will unselect any other options if requested so (and selected).
     *
     * @param {Category} category Category to select
     * @param {boolean} unselectOther If true then will unselect other options (default false)
     */
    toggle(category: Category, unselectOther: boolean = false): void {
        if (this.isSelected(category)) {
            this.unselect(category);
        } else {
            this.select(category, unselectOther);
        }
    }

    /**
     * Checks if option is currently selected.
     */
    isSelected(category: Category): boolean {
        return this._selection.indexOf(category.id) !== -1;
    }

    /**
     * Applies filter to models collection
     */
    applyFilter(collection: T[]): T[] {
        if (!this.hasSelection) {
            return collection;
        }

        // filter collection
        return collection.filter(model => {
            const categoryIds: number[] | undefined = model.data ? model.data['category_ids'] : undefined;

            // if categoryIds is not set then false
            if (categoryIds === undefined) {
                return false;
            }

            return !!intersection(<number[]>categoryIds, <number[]>this.selection).length;
        });
    }

    /**
     * Selection get / set
     */
    override get selection(): number[] | undefined {
        return this._selection.length ? this._selection : undefined;
    }

    override set selection(data: number[] | undefined) {
        this._selection = data || [];
    }

    /**
     * Checks if filter is applied
     * @return {boolean}
     */
    get hasSelection(): boolean {
        return !!this._selection.length;
    }

    getState(): any[] {
        return this._selection;
    }

    setState(data: string | number | (string | number)[]) {
        if (!data) {
            return;
        }

        if (Array.isArray(data)) {
            this.selection = <number[]>data;
        } else {
            this.selection = (<string>data).split(',').map(i => parseInt(i, 10));
        }
    }
}
